import type { FilterValue } from 'antd/es/table/interface';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import useSearchParamValues, {
  type TParamFormatters
} from '@/hooks/useSearchParamValues';
import { stringToCamelCase } from '@/services';
import type { TPaginationParams } from '@/types/api';

import useDebouncedValue from './useDebouncedValue';

type TProps<F extends string, Formatters extends TParamFormatters<F>> = {
  extraParamNames?: F[];
  formatters?: Formatters;
  limitParamName?: string;
  pageParamName?: string;
  searchTermDelay?: number;
  searchTermFormatter?: (value: string) => string;
  searchTermParamName?: string;
};

const DEFAULT_LIMIT = 10;
const DEFAULT_PAGE = 1;

const usePaginationParams = <
  F extends string,
  Formatters extends TParamFormatters<F>
>(
  props?: TProps<F, Formatters>
) => {
  const {
    extraParamNames = [] as unknown as F[],
    formatters = {} as Formatters,
    limitParamName = 'limit',
    pageParamName = 'page',
    searchTermDelay,
    searchTermFormatter,
    searchTermParamName = 'searchTerm'
  } = props || {};

  const camelCasedLimitParamName = stringToCamelCase(limitParamName);
  const camelCasedPageParamName = stringToCamelCase(pageParamName);
  const camelCasedSearchTermParamName = stringToCamelCase(searchTermParamName);

  const [searchParams, setSearchParams] = useSearchParams();

  const paginationRowValues = useSearchParamValues(
    [limitParamName, pageParamName, searchTermParamName],
    { delay: -1, formatters: { [searchTermParamName]: searchTermFormatter } }
  );

  const extraParams = useSearchParamValues(extraParamNames, {
    formatters,
    delay: -1
  });

  const debouncedSearchValue = useDebouncedValue(
    paginationRowValues[camelCasedSearchTermParamName],
    searchTermDelay
  );

  const [searchValue, setSearchValue] = useState(
    paginationRowValues[camelCasedSearchTermParamName]
  );

  const paginationParams: TPaginationParams = useMemo(() => {
    return {
      limit:
        Number(paginationRowValues[camelCasedLimitParamName]) || DEFAULT_LIMIT,
      page:
        Number(paginationRowValues[camelCasedPageParamName]) || DEFAULT_PAGE,
      searchTerm: paginationRowValues[camelCasedSearchTermParamName]
    };
  }, [paginationRowValues, searchTermFormatter]);

  const handleSearch = (value: string) => {
    const formattedSearchValue: string = searchTermFormatter
      ? searchTermFormatter(value)
      : value;

    setSearchValue(formattedSearchValue);

    const params = new URLSearchParams(searchParams);

    params.delete(pageParamName);

    if (!formattedSearchValue) params.delete(searchTermParamName);
    else params.set(searchTermParamName, formattedSearchValue);

    setSearchParams(params);
  };

  const handlePageChange = (currentPage: number, itemsPerPage: number) => {
    const params = new URLSearchParams(searchParams);

    if (itemsPerPage === DEFAULT_LIMIT) params.delete(limitParamName);
    else params.set(limitParamName, itemsPerPage.toString());

    if (currentPage === DEFAULT_PAGE) params.delete(pageParamName);
    else params.set(pageParamName, currentPage.toString());

    setSearchParams(params);
  };

  const createFiltersChangeHandler =
    (
      filtersHandler: (
        filters: Record<string, FilterValue | null>,
        params: URLSearchParams
      ) => void
    ) =>
    (filters: Record<string, FilterValue | null>) => {
      const params = new URLSearchParams(searchParams);

      filtersHandler(filters, params);

      const { limit = DEFAULT_LIMIT } = paginationParams;
      if (limit === DEFAULT_LIMIT) params.delete(limitParamName);
      else params.set(limitParamName, limit.toString());

      params.delete(pageParamName);

      setSearchParams(params);
    };

  return {
    createFiltersChangeHandler,
    debouncedSearchValue,
    extraParams,
    handlePageChange,
    handleSearch,
    paginationParams,
    searchValue
  };
};

export default usePaginationParams;
